import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { usePortalClient } from '../hooks/usePortalClient'
import { usePortalNavigation } from '../hooks/usePortalNavigation'
import { useAuth } from '../../../contexts/AuthContext'
import { useClientIntake } from '../../assessment/hooks/useClientIntake'
import { supabase } from '../../../shared/lib/supabase'
import { calculateBenchmarkGap } from '../../../shared/utils/scoring'
import { getMarketSegmentId } from '../../../shared/utils/marketSegment'
import ProgramSnapshot from '../components/ProgramSnapshot'
import ActivityTimeline from '../components/ActivityTimeline'
import { relativeTime } from '../../../shared/utils/clientHelpers'

const MATURITY_LEVELS = [
  { level: 1, label: 'Initial' },
  { level: 2, label: 'Developing' },
  { level: 3, label: 'Defined' },
  { level: 4, label: 'Managed' },
  { level: 5, label: 'Optimizing' },
]

function getMaturityInfo(score) {
  if (score == null) return null
  if (score <= 20) return MATURITY_LEVELS[0]
  if (score <= 40) return MATURITY_LEVELS[1]
  if (score <= 60) return MATURITY_LEVELS[2]
  if (score <= 80) return MATURITY_LEVELS[3]
  return MATURITY_LEVELS[4]
}

function PortalHome() {
  const { client, clientId, loading } = usePortalClient()
  const { isRetainer, basePath } = usePortalNavigation()
  const { profile } = useAuth()
  const { status: intakeStatus } = useClientIntake(clientId, client)
  const pendingPolicies = 0
  const [activityEvents, setActivityEvents] = useState([])

  const completedAssessments = (client?.assessments || [])
    .filter(a => a.status === 'completed')
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
  const activeAssessments = (client?.assessments || []).filter(a => a.status === 'in_progress')

  const latestAssessment = completedAssessments[0]
  const overallScore = latestAssessment?.scores?.overall?.score
  const maturityLevel = latestAssessment?.scores?.overall?.maturityLevel
  const maturityInfo = getMaturityInfo(overallScore)

  // Calculate benchmark gap
  let benchmarkGap = null
  if (overallScore != null && client?.industry) {
    const segment = getMarketSegmentId(client.staffingSize, client.annualRevenue)
    const gap = calculateBenchmarkGap(overallScore, client.industry, segment)
    if (gap != null) benchmarkGap = Math.round(gap)
  }

  // Build activity events
  useEffect(() => {
    if (!clientId) return
    const events = []

    completedAssessments.slice(0, 2).forEach(a => {
      events.push({
        id: `completed-${a.id}`,
        color: '#22C55E',
        action: 'Assessment completed',
        detail: a.title || 'Discovery assessment',
        time: relativeTime(a.completedAt)
      })
    })

    activeAssessments.slice(0, 1).forEach(a => {
      events.push({
        id: `started-${a.id}`,
        color: '#F59E0B',
        action: 'Assessment started',
        detail: a.title || 'Discovery assessment',
        time: relativeTime(a.createdAt)
      })
    })

    supabase
      .from('maester_reports')
      .select('id, file_name, created_at')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false })
      .limit(2)
      .then(({ data }) => {
        if (data) {
          data.forEach(r => {
            events.push({
              id: `report-${r.id}`,
              color: '#6366F1',
              action: 'M365 report uploaded',
              detail: r.file_name,
              time: relativeTime(r.created_at)
            })
          })
        }
        setActivityEvents([...events])
      })
  }, [clientId])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-400" />
      </div>
    )
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Client not found</p>
      </div>
    )
  }

  // First name for greeting
  const firstName = profile?.full_name?.split(' ')[0] || client.name

  // Build action items
  const actionItems = []
  if (completedAssessments.length > 0) {
    actionItems.push({
      id: 'view-results',
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      title: 'Your assessment results are ready to review',
      description: latestAssessment?.completedAt
        ? `Completed ${new Date(latestAssessment.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — your advisor has released the findings`
        : 'Your security assessment has been completed.',
      link: `${basePath}/results`,
      cta: 'View Results',
      urgency: 'info'
    })
  }
  if (isRetainer && pendingPolicies > 0) {
    actionItems.push({
      id: 'review-policies',
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      title: `${pendingPolicies} security policies awaiting your review`,
      description: 'Review and acknowledge each policy to confirm your understanding.',
      link: `${basePath}/policies`,
      cta: 'Review Policies',
      urgency: 'high'
    })
  }

  const urgencyColors = { high: '#F59E0B', info: '#3B82F6' }
  const engagementLabel = isRetainer ? 'Retainer' : 'Discovery'

  return (
    <div className="space-y-6">
      {/* Intake status banner */}
      {intakeStatus === 'pending' && (
        <Link to={`${basePath}/intake`} className="block">
          <div className="rounded-lg p-4 flex items-center gap-4 transition-colors hover:shadow-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-l-4 border-l-primary-400">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-cyan-50 dark:bg-cyan-900/20">
              <svg className="w-5 h-5 text-cyan-600 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Complete Your Organization Profile</p>
              <p className="text-xs mt-0.5 text-gray-500 dark:text-gray-400">Help us prepare for your security assessment by providing some basic information about your organization. This typically takes about 10 minutes.</p>
            </div>
            <span className="text-sm font-medium flex-shrink-0 flex items-center gap-1 text-cyan-600 dark:text-cyan-400">
              Get Started
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </span>
          </div>
        </Link>
      )}
      {intakeStatus === 'unlocked' && (
        <Link to={`${basePath}/intake`} className="block">
          <div className="rounded-lg p-4 flex items-center gap-4 transition-colors hover:shadow-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 border-l-4 border-l-amber-500">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-amber-100 dark:bg-amber-900/30">
              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Organization Profile Reopened for Editing</p>
              <p className="text-xs mt-0.5 text-amber-800 dark:text-amber-300">Your advisor has requested updates to your organization profile.</p>
            </div>
            <span className="text-sm font-medium flex-shrink-0 flex items-center gap-1 text-amber-600 dark:text-amber-400">
              Review & Update
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </span>
          </div>
        </Link>
      )}

      {/* Welcome banner with inline score hero */}
      <div className="rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #012438 0%, #213C4E 100%)' }}>
        <div className="p-7">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-xl font-bold text-white">Welcome back, {firstName}</h1>
              <p className="text-sm mt-1 text-gray-300">
                {client.name} — Managed by Sidewalk Security Advisors
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xs uppercase tracking-widest text-gray-400">Engagement</p>
              <p className={`text-sm font-semibold ${isRetainer ? 'text-primary-400' : 'text-amber-400'}`}>
                {engagementLabel} — Active
              </p>
            </div>
          </div>

          {/* Score hero inline */}
          {overallScore != null ? (
            <div className="flex items-center gap-5 mt-6">
              <div className="w-20 h-20 rounded-2xl flex flex-col items-center justify-center flex-shrink-0" style={{ background: 'rgba(138, 208, 214, 0.15)', border: '2px solid rgba(138, 208, 214, 0.3)' }}>
                <span className="text-3xl font-extrabold leading-none text-primary-400">{Math.round(overallScore)}</span>
                <span className="text-xs mt-0.5 text-gray-300">out of 100</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">
                  Security Maturity: Level {maturityInfo?.level} — {maturityInfo?.label || maturityLevel}
                </p>
                <p className="text-xs mt-1 leading-relaxed text-gray-300">
                  Your organization has established security processes and controls. You are performing
                  {benchmarkGap != null && benchmarkGap >= 0 ? ' above average' : benchmarkGap != null ? ' below average' : ' competitively'} compared
                  to {client.industry || 'similar'} organizations of similar size.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 mt-5 p-3.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <svg className="w-5 h-5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-gray-300">Assessment in progress</p>
                <p className="text-xs text-gray-400">Results will appear here once your advisor completes the review.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Items */}
      {actionItems.length > 0 ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest mb-3 text-gray-400 dark:text-gray-500">Action Items</p>
          <div className="space-y-3">
            {actionItems.map(item => {
              const color = urgencyColors[item.urgency]
              return (
                <div
                  key={item.id}
                  className="bg-white dark:bg-gray-800 rounded-xl p-4 flex items-center gap-4 border border-gray-200 dark:border-gray-700"
                  style={{ borderLeft: `3px solid ${color}` }}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + '10' }}>
                    <svg className="w-5 h-5" style={{ color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{item.title}</p>
                    <p className="text-xs mt-0.5 leading-relaxed text-gray-500 dark:text-gray-400">{item.description}</p>
                  </div>
                  <Link
                    to={item.link}
                    className="flex-shrink-0 text-xs font-semibold px-4 py-2 rounded-lg text-white whitespace-nowrap"
                    style={{ backgroundColor: color }}
                  >
                    {item.cta}
                  </Link>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <svg className="w-5 h-5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm font-medium text-green-700 dark:text-green-400">You're all caught up — no action items right now.</p>
        </div>
      )}

      {/* Program Snapshot */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest mb-3 text-gray-400 dark:text-gray-500">Program Snapshot</p>
        <ProgramSnapshot
          engagementType={client.status || 'discovery'}
          benchmarkGap={benchmarkGap}
          client={client}
          enablementProgress={0}
          enablementInitiativeCount={completedAssessments.length > 0 ? 'available' : null}
        />
      </div>

      {/* Activity Timeline */}
      <ActivityTimeline events={activityEvents} />
    </div>
  )
}

export default PortalHome
